import React from 'react'
import { Tooltip } from '@surecompanies/core_components'
import { InfoCircleOutlined } from '@ant-design/icons'
import { GRID_TEMPLATE_COLUMNS, SortColumnKey, SortOrder } from '../helpers'
import styles from './TransactionsTable.module.scss'

/**
 * Sticky header row for the transaction grid. Only columns backed by the
 * original all_payment_transaction table are sortable — those render as clickable
 * sort <button>s (native button = built-in keyboard/focus/role support) that call
 * `onToggleSort(key)` and show a ▲/▼ indicator. The benefit/allocation-derived
 * columns and the trailing action columns ("Action", "Log") are static
 * (non-sortable) header cells, since sorting them required joins.
 */

interface Column {
  label: string
  /** Present only for sortable columns (original-table columns). */
  key?: SortColumnKey
  numeric?: boolean
  /** Header hover hint explaining what the column shows. */
  tooltip?: React.ReactNode
}

// Scope of the column (and the note search): only notes on the tx's own
// absorb/allocate/return actions — not benefit-level notes.
const NOTE_TOOLTIP =
  'Searches notes from absorb, allocate, and refund actions on this transaction only. Other notes not related to the transaction, such as explicit or set expected price notes, are not included.'

const COLUMNS: Column[] = [
  { label: 'Transaction Number', key: 'transactionNumber' },
  { label: 'Transaction ID', key: 'transactionId' },
  { label: 'Payment Type', key: 'paymentType' },
  { label: 'Amount', key: 'amount', numeric: true },
  { label: 'Date', key: 'date' },
  { label: 'Carrier' },
  { label: 'Benefit ID' },
  { label: 'Company EE ID' },
  { label: 'Company Name' },
  { label: 'Insured Person Name' },
  { label: 'Month' },
  { label: 'Recon' },
  { label: 'Transaction Note', tooltip: NOTE_TOOLTIP },
  { label: 'Ticket' },
]

interface TableHeadProps {
  sortBy: SortColumnKey | undefined
  sortOrder: SortOrder | undefined
  onToggleSort: (key: SortColumnKey) => void
}

const SortIndicator: React.FC<{ dir: SortOrder | null }> = ({ dir }) => (
  <span className={styles.sortIndicator} aria-hidden="true">
    <span className={dir === 'asc' ? styles.sortArrowActive : undefined}>
      ▲
    </span>
    <span className={dir === 'desc' ? styles.sortArrowActive : undefined}>
      ▼
    </span>
  </span>
)

const TableHead: React.FC<TableHeadProps> = ({
  sortBy,
  sortOrder,
  onToggleSort,
}) => (
  <div
    className={styles.headerRow}
    style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
  >
    {COLUMNS.map((col) => {
      // Non-sortable (benefit/allocation-derived) column → plain header cell.
      if (!col.key) {
        return (
          <span
            key={col.label}
            className={`${styles.headerCellStatic}${col.numeric ? ` ${styles.num}` : ''}`}
          >
            {col.label}
            {col.tooltip && (
              <Tooltip title={col.tooltip} placement="top">
                <InfoCircleOutlined
                  className={styles.headerInfoIcon}
                  aria-label={`About ${col.label}`}
                />
              </Tooltip>
            )}
          </span>
        )
      }
      // Capture in a local so the arrow closures keep the non-undefined
      // narrowing (property access `col.key` widens back inside a closure).
      const key = col.key
      const isActive = sortBy === key
      return (
        <button
          key={key}
          type="button"
          className={`${styles.headerCell}${col.numeric ? ` ${styles.num}` : ''}`}
          aria-sort={
            isActive
              ? sortOrder === 'asc'
                ? 'ascending'
                : 'descending'
              : 'none'
          }
          onClick={() => onToggleSort(key)}
        >
          <span>{col.label}</span>
          <SortIndicator dir={isActive ? (sortOrder ?? null) : null} />
        </button>
      )
    })}
    {/* Action + Log — non-sortable action columns (no backend SortColumnKey),
        rendered as plain header cells outside the sortable-button loop above.
        Action = allocate/edit; Log = per-tx event history. */}
    <span className={styles.headerCellStatic}>Action</span>
    <span className={styles.headerCellStatic}>Log</span>
  </div>
)

export default TableHead
